import React, { useState } from 'react';
import cn from 'class-names';

import './SparklesIcon.css';

export default function SparklesIcon({ className = '' }: { className?: string }): JSX.Element {
  const [rotatingSparkle, setRotatingSparkle] = useState<number | null>(null);

  const handleSparkleClick = (sparkleIndex: number) => {
    if (rotatingSparkle === null) {
      setRotatingSparkle(sparkleIndex);
    }
  };

  const handleAnimationEnd = () => {
    setRotatingSparkle(null);
  };

  return (
    <svg
      width="124"
      height="124"
      viewBox="0 0 124 124"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ perspective: '400px' }}
    >
      <defs>
        <linearGradient
          id="gradient1"
          x1="43"
          y1="50"
          x2="70"
          y2="84"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#CCACFF">
            <animate
              attributeName="stop-color"
              values="#CCACFF;#A8D4FF;#CCACFF"
              dur="3s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="1" stopColor="#221CB6">
            <animate
              attributeName="stop-color"
              values="#221CB6;#6B21A8;#221CB6"
              dur="3s"
              repeatCount="indefinite"
            />
          </stop>
        </linearGradient>
        <linearGradient
          id="gradient2"
          x1="60"
          y1="62"
          x2="82"
          y2="86"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#CCACFF">
            <animate
              attributeName="stop-color"
              values="#A8D4FF;#CCACFF;#A8D4FF"
              dur="2.5s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="1" stopColor="#221CB6">
            <animate
              attributeName="stop-color"
              values="#6B21A8;#221CB6;#6B21A8"
              dur="2.5s"
              repeatCount="indefinite"
            />
          </stop>
        </linearGradient>
        <linearGradient
          id="gradient3"
          x1="72"
          y1="41"
          x2="85"
          y2="55"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#CCACFF">
            <animate
              attributeName="stop-color"
              values="#DDA0FF;#CCACFF;#DDA0FF"
              dur="2s"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="1" stopColor="#221CB6">
            <animate
              attributeName="stop-color"
              values="#4C1D95;#221CB6;#4C1D95"
              dur="2s"
              repeatCount="indefinite"
            />
          </stop>
        </linearGradient>
      </defs>

      {/* Large sparkle - main left */}
      <g
        onClick={() => handleSparkleClick(1)}
        onAnimationEnd={rotatingSparkle === 1 ? handleAnimationEnd : undefined}
        className={cn('cursor-pointer origin-center', rotatingSparkle === 1 && 'sparkle-rotate-3d')}
        style={{ transformOrigin: '46px 54px', transformStyle: 'preserve-3d' }}
      >
        <path
          d="M46.4287 39C47.1106 39.0001 47.7649 39.2708 48.2471 39.7529C48.7292 40.2351 49 40.8894 49 41.5713C49 44.2992 50.0837 46.9158 52.0127 48.8447C53.9415 50.7735 56.5575 51.8573 59.2852 51.8574C59.9671 51.8574 60.6213 52.1282 61.1035 52.6104C61.5858 53.0926 61.8574 53.7467 61.8574 54.4287C61.8574 55.1107 61.5857 55.7649 61.1035 56.2471C60.6213 56.7291 59.967 57 59.2852 57C56.5574 57.0001 53.9415 58.0839 52.0127 60.0127C50.0837 61.9416 49 64.5582 49 67.2861C48.9999 67.968 48.7292 68.6223 48.2471 69.1045C47.7649 69.5866 47.1106 69.8573 46.4287 69.8574C45.7469 69.8574 45.0926 69.5865 44.6104 69.1045C44.1282 68.6223 43.8575 67.968 43.8574 67.2861C43.8574 64.5583 42.7736 61.9416 40.8447 60.0127C38.9158 58.0838 36.2992 57 33.5713 57C32.8893 57 32.2352 56.7293 31.7529 56.2471C31.2707 55.7649 31 55.1107 31 54.4287C31 53.7467 31.2707 53.0926 31.7529 52.6104C32.2352 52.1281 32.8893 51.8574 33.5713 51.8574C36.2992 51.8574 38.9158 50.7737 40.8447 48.8447C42.7736 46.9158 43.8574 44.2992 43.8574 41.5713C43.8575 40.8894 44.1281 40.2351 44.6104 39.7529C45.0926 39.2708 45.7468 39 46.4287 39Z"
          fill="url(#gradient1)"
          className="sparkle-pulse-1"
        />
      </g>

      {/* Medium sparkle - bottom right */}
      <g
        onClick={() => handleSparkleClick(2)}
        onAnimationEnd={rotatingSparkle === 2 ? handleAnimationEnd : undefined}
        className={cn('cursor-pointer origin-center', rotatingSparkle === 2 && 'sparkle-rotate-3d')}
        style={{ transformOrigin: '71px 74px', transformStyle: 'preserve-3d' }}
      >
        <path
          d="M70.8574 62.1436C71.3687 62.1436 71.8591 62.3465 72.2207 62.708C72.5824 63.0697 72.7861 63.5608 72.7861 64.0723C72.7862 66.1179 73.5986 68.0798 75.0449 69.5264C76.4916 70.973 78.4541 71.7861 80.5 71.7861C81.0115 71.7861 81.5026 71.9899 81.8643 72.3516C82.2257 72.7132 82.4287 73.2035 82.4287 73.7148C82.4287 74.2261 82.2257 74.7165 81.8643 75.0781C81.5026 75.4398 81.0115 75.6436 80.5 75.6436C78.4541 75.6436 76.4916 76.4567 75.0449 77.9033C73.5986 79.3499 72.7862 81.3118 72.7861 83.3574C72.7861 83.8689 72.5824 84.36 72.2207 84.7217C71.8591 85.0832 71.3688 85.2861 70.8574 85.2861C70.3459 85.2861 69.8548 85.0834 69.4932 84.7217C69.1316 84.36 68.9287 83.8689 68.9287 83.3574C68.9286 81.3116 68.1156 79.35 66.6689 77.9033C65.2223 76.4567 63.2607 75.6437 61.2148 75.6436C60.7034 75.6436 60.2123 75.4398 59.8506 75.0781C59.4891 74.7165 59.2861 74.2262 59.2861 73.7148C59.2861 73.2035 59.4891 72.7132 59.8506 72.3516C60.2123 71.9899 60.7034 71.7861 61.2148 71.7861C63.2607 71.786 65.2223 70.973 66.6689 69.5264C68.1156 68.0797 68.9286 66.1181 68.9287 64.0723C68.9287 63.5608 69.1315 63.0697 69.4932 62.708C69.8548 62.3463 70.3459 62.1436 70.8574 62.1436Z"
          fill="url(#gradient2)"
          className="sparkle-pulse-2"
        />
      </g>

      {/* Small sparkle - top right */}
      <g
        onClick={() => handleSparkleClick(3)}
        onAnimationEnd={rotatingSparkle === 3 ? handleAnimationEnd : undefined}
        className={cn('cursor-pointer origin-center', rotatingSparkle === 3 && 'sparkle-rotate-3d')}
        style={{ transformOrigin: '79px 48px', transformStyle: 'preserve-3d' }}
      >
        <path
          d="M78.5713 41.5713C78.9975 41.5713 79.4066 41.7406 79.708 42.042C80.0093 42.3434 80.1787 42.7526 80.1787 43.1787C80.1788 44.0311 80.5174 44.8484 81.1201 45.4512C81.7229 46.0539 82.5401 46.3926 83.3926 46.3926C83.8188 46.3926 84.2279 46.5619 84.5293 46.8633C84.8307 47.1647 85 47.5738 85 48C85 48.4262 84.8307 48.8353 84.5293 49.1367C84.2279 49.4381 83.8188 49.6074 83.3926 49.6074C82.5401 49.6074 81.7229 49.9461 81.1201 50.5488C80.5173 51.1516 80.1787 51.9688 80.1787 52.8213C80.1787 53.2475 80.0093 53.6566 79.708 53.958C79.4066 54.2594 78.9975 54.4287 78.5713 54.4287C78.1451 54.4287 77.736 54.2594 77.4346 53.958C77.1332 53.6566 76.9639 53.2475 76.9639 52.8213C76.9638 51.9689 76.6252 51.1516 76.0225 50.5488C75.4197 49.9461 74.6024 49.6075 73.75 49.6074C73.3238 49.6074 72.9147 49.438 72.6133 49.1367C72.3119 48.8353 72.1426 48.4262 72.1426 48C72.1426 47.5738 72.3119 47.1647 72.6133 46.8633C72.9147 46.562 73.3238 46.3926 73.75 46.3926C74.6024 46.3925 75.4197 46.0539 76.0225 45.4512C76.6252 44.8484 76.9638 44.0311 76.9639 43.1787C76.9639 42.7525 77.1332 42.3434 77.4346 42.042C77.736 41.7406 78.145 41.5713 78.5713 41.5713Z"
          fill="url(#gradient3)"
          className="sparkle-pulse-3"
        />
      </g>
    </svg>
  );
};
